import { Box, Text } from '@mantine/core';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import { useTranscript } from 'src/meetings/hooks/useTranscript';
import { useTranscriptAudio } from 'src/meetings/hooks/useTranscriptAudio';
import type { CalendarEvent } from 'src/meetings/models';
import PageLoader from 'src/UI/loader/PageLoader';
import { AudioPlayer } from './transcript/AudioPlayer';
import { TranscriptSegmentsList } from './transcript/TranscriptSegmentsList';

type Props = {
  event: CalendarEvent;
};

export const TranscriptTab = ({ event }: Props) => {
  const { transcript, isLoading, isError, error } = useTranscript({
    eventId: event.id,
    enabled: event.hasTranscription,
  });

  const audioControls = useTranscriptAudio({
    fileId: transcript?.file?.id,
    segments: transcript?.segments ?? [],
    eventId: event.id,
  });

  if (isLoading && event.hasTranscription) {
    return <PageLoader absolute={false} />;
  }

  if (isError) {
    return (
      <GenericErrorPage
        error={error}
        title="Unable to Load Transcript"
        text="We couldn't load the transcript for this meeting. Please try again later or contact support if the problem persists."
      />
    );
  }

  if (audioControls.isLoadingAudio && event.hasTranscription) {
    return <PageLoader />;
  }

  if (!event.hasTranscription || !transcript) {
    return (
      <Box p="md">
        <Text size="sm" c="gray.7">
          No transcript available for this meeting.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <TranscriptSegmentsList
        segments={transcript.segments}
        selectedIndex={audioControls.selectedSegmentIndex ?? 0}
        onSegmentClick={audioControls.handleSegmentClick}
      />

      {!audioControls.hasAudioError && (
        <AudioPlayer
          isPlaying={audioControls.isPlaying}
          currentTime={audioControls.currentTime}
          duration={audioControls.duration}
          isMuted={audioControls.isMuted}
          onPlayPause={audioControls.handlePlayPause}
          onSeek={audioControls.handleSeek}
          onMuteToggle={audioControls.handleMuteToggle}
        />
      )}
    </Box>
  );
};
